import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import { Textarea } from "@/v5/components/ui/textarea";
import { HeartPulse, Loader2, Send } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "@/store/toastStore";

interface HealthNexusComposeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientName: string;
  /** Sends the message to the patient's Next Health portal chat (as the care team). */
  onSend: (content: string) => Promise<void>;
}

/**
 * Compose + send a HealthNexus message to the patient. Mirrors NoteComposeModal
 * but there is no subject/recipients/reply — this goes to the patient's Next
 * Health portal provider chat and appears there as a care-team message.
 */
export function HealthNexusComposeModal({
  open,
  onOpenChange,
  patientName,
  onSend,
}: HealthNexusComposeModalProps) {
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const reset = useCallback(() => {
    setContent("");
    setSending(false);
  }, []);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!v) reset();
      onOpenChange(v);
    },
    [onOpenChange, reset],
  );

  const handleSend = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Message is required");
      return;
    }
    setSending(true);
    try {
      await onSend(trimmed);
      toast.success("Message sent");
      handleOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }, [content, onSend, handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="h-5 w-5 text-blue-600" />
            Send HealthNexus message
          </DialogTitle>
          <DialogDescription>
            {`Send a message to ${patientName} in their Next Health portal. It appears to the patient as a care-team message.`}
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Type your message to the patient..."
          className="min-h-[180px] resize-y"
          maxLength={8000}
          autoFocus
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !content.trim()}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
